import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import React, { FC, MutableRefObject, useContext, useState } from 'react'

import { Link } from '@Components/Link'
import { useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { ProductContext } from '@Components/Product/Context'
import { SizeOutOfStock } from '@Components/SizeModal/SizeOutOfStock'
import { BuyButtonArea } from '@Domains/Product/Features/Display/BuyButtonArea/BuyButtonArea'
import { useChipAssets } from '@Domains/Product/Features/Display/ChipAssets'
import ProductChipsLayout from '@Domains/Product/Features/Display/ProductChipsLayout/ProductChipsLayout'
import { ProductDescription } from '@Domains/Product/Features/Display/ProductDescription/ProductDescription'
import { ProductReviews } from '@Domains/Product/Features/Display/ProductReviews/ProductReviews'
import { ProductShipping } from '@Domains/Product/Features/Display/ProductShipping'
import { ProductSpecifications } from '@Domains/Product/Features/Display/ProductSpecifications/ProductSpecifications'
import { StarRating } from '@Domains/Product/Features/Display/StarRating/StarRating'
import { TitleAndPrice } from '@Domains/Product/Features/Display/TitleAndPrice/TitleAndPrice'
import ProductTactics from '@Domains/Product/Features/Tactics/ProductTactics'
import { Product } from '@Generated/graphql'
import { AnchorItemInjections } from '@Globals/Injections/Client'
import { useFilteredVariants } from '@Globals/Product/useFilteredVariants'

import styles from './Info.module.scss'

export interface BuyQuantityProducts {
  addition?: number
  key?: string
  percentage?: number
  quantity: number
}

interface InfoProps {
  reviewWrapperRef?: MutableRefObject<HTMLDivElement | null>
}

export const Info: FC<InfoProps> = ({ reviewWrapperRef = null }) => {
  const product = useContext(ProductContext)
  const [selectedRelatedProductIDs, setSelectedRelatedProductIDs] = useState<string[]>([])
  const { price } = product?.masterVariant || {}
  // Some calculations needed for the "buy two for almost-one" offer
  const {
    availableVariants,
    variantsInTotal,
  } = useFilteredVariants({ product })
  const atLeastPartiallyInStock = product?.availability?.isInStock

  // Preselect the first variant if there's only one anyway. If there are more, even if they're filtered out due to not
  // being restocked, we're dealing with a product that's supposed to come in many sizes, so the user will want to know
  // which size they're putting in the basket.
  const [selectedVariantID] = useState<string>(
    variantsInTotal === 1 ? first(availableVariants)?.id || '' : '',
  )
  const [selectedCrossSellIDs, setSelectedCrossSellIDs] = useState<Product['id'][]>([])
  const [buyQuantitySelection] = useState<BuyQuantityProducts>({ quantity: 1 })

  const [buyPersonalizationId, setBuyPersonalizationId] = useState<string | null>(null)
  const reviewSidePaneRef = useNativeModalActions()

  const chipOptions = useChipAssets(product)

  if (!product) {
    return <>404</>
  }

  return (
    <>
      <div className={styles.infoHeader}>
        <div>
          {product.brand && product.brand.logo && (
            <Link className={styles.brand} href={product.brand.url?.toString()}>
              <img
                alt={product.brand.id || ''}
                src={product.brand.logo || ''}
                title={product.brand.title || ''}
              />
            </Link>
          )}
          {product.brand?.title && !product.brand.logo && (
            <Link className={styles.brand} href={product.brand.url?.toString()}>
              {product.brand.title}
            </Link>
          )}

          <TitleAndPrice
            id={product.id}
            name={product.name}
            price={price || undefined}
          />

          <StarRating
            className={styles.rating}
            openReviews={() => reviewSidePaneRef.current?.open()}
            rating={product?.productReviews?.averageRating || 0}
            ratingConcise
            ratingCount={product?.productReviews?.count && product.productReviews.count >= 20 ? product.productReviews.count : undefined}
          />

          <ProductChipsLayout className={styles.chips} maxNumberOfTags={3} options={chipOptions} />
        </div>

        <ProductTactics
          addonsSelected={!isEmpty(selectedRelatedProductIDs) || !isEmpty(selectedCrossSellIDs) || !isEmpty(buyPersonalizationId)}
          buyPersonalizationID={buyPersonalizationId}
          group="gold"
          onBuyPersonalizationID={setBuyPersonalizationId}
          selectedCrossSellIDs={selectedCrossSellIDs}
          selectedRelatedProductIDs={selectedRelatedProductIDs}
          setSelectedCrossSellIDs={setSelectedCrossSellIDs}
          setSelectedRelatedProductIDs={setSelectedRelatedProductIDs}
        />
        {atLeastPartiallyInStock && <AnchorItemInjections anchor="buy_button.before" />}
      </div>

      {atLeastPartiallyInStock && (
        <BuyButtonArea
          buyPersonalizationID={buyPersonalizationId}
          buyQuantitySelection={buyQuantitySelection}
          className={styles.buyButton}
          product={product}
          selectedCrossSellIDs={selectedCrossSellIDs.concat(selectedRelatedProductIDs)}
          selectedVariantID={selectedVariantID}
          variantsInTotal={variantsInTotal}
        />
      )}

      <div className={styles.infoFooter}>
        {atLeastPartiallyInStock && <AnchorItemInjections anchor="buy_button.after" />}
        {atLeastPartiallyInStock && <ProductShipping />}
        {!atLeastPartiallyInStock && <SizeOutOfStock className={styles.outOfStock} product={product} showSizeSelector />}

        <ProductTactics group="temporary" />

        <div className={styles.accordions}>
          <ProductDescription product={product} />
          <ProductSpecifications product={product} selectedVariantID={selectedVariantID} />

          {product.productReviews && product.productReviews.count > 0
            && (
              <div ref={reviewWrapperRef}>
                <ProductReviews reviewSidePaneRef={reviewSidePaneRef} reviews={product.productReviews} />
              </div>
            )}
        </div>
      </div>
    </>
  )
}
